/**
 * utils/math.ts — PURE math utilities (SPEC §6: "clamp, sum, round…").
 *
 * Boundary rule (SPEC §6, PLAN §1.2): `utils/` NEVER import three.js,
 * is pure and stateless — deterministic, unit-testable functions.
 *
 * Structure note (fix P8): SPEC §6 expects `utils/math.ts`; here they are
 * general NUMERIC utilities (`clamp`, `roundTo`). The `sum` is on purpose
 * left in `transforms/vectors.ts` (where it already lived and where they are also needed
 * the other vector/quaternion utilities) to avoid unnecessary duplication.
 *
 * Constrained 3D configurator: each function is commented for maintainability.*/

/**
 * Clamp a number to `[min, max]` (inclusive).
 * `Infinity`/`−Infinity` are allowed for single-sided clamps.
 *
 * @param value Value to clamp.
 * @param min Lower bound (inclusive).
 * @param max Upper limit (inclusive).
 * @returns `min` if value < min, `max` if value > max, otherwise value.*/
export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/**
 * Rounds a number to `decimals` decimal places.
 * standard `Math.round` (half-up); used for PLAN numeric criteria
 * (e.g. stagger === 13.1 without floating-point noise).
 *
 * @param value Value to round.
 * @param decimals Decimal places (default 0).
 * @returns Rounded value.*/
export function roundTo(value: number, decimals = 0): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * Distanza a cui una sfera di raggio `radius` riempie il campo verticale di una
 * camera prospettica di apertura `fovDeg`, moltiplicata per il margine d'aria.
 * Pura: nessun three.js, nessun dominio 3D (regola di confine di utils/).
 *
 * @param radius Raggio della sfera da inquadrare (mm).
 * @param fovDeg Apertura verticale della camera (gradi).
 * @param marginFactor Aria attorno al soggetto (1 = aderente).
 * @returns Distanza camera-soggetto in mm.*/
export function focusDistanceMm(radius: number, fovDeg: number, marginFactor: number): number {
  return (radius / Math.sin((fovDeg * Math.PI) / 360)) * marginFactor;
}
